import { useEffect, useState } from "react";
import { api } from "../api";
import FileLink from "../components/FileLink";
import { Badge, Card, Spinner } from "../components/Shared";
import { useDocumentWord } from "../documentWord";

/**
 * Every published document in every group, for a global admin.
 *
 * The facility-wide answer to "what are people being asked to agree to", which
 * nothing could give: `DocumentsPanel` shows one group at a time, and a policy
 * is otherwise only visible by opening the group that owns it.
 *
 * The archive link is the part with teeth. It carries every signature — names,
 * addresses, times and IP addresses — so the server writes a non-best-effort
 * audit row before handing any of it over, and refuses anybody who is not a
 * global admin. This panel only decides what is *shown*.
 */
export default function AdminDocumentsPanel() {
  const word = useDocumentWord();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/api/admin/documents").then(setRows).catch(e => setError(e.message));
  }, []);

  if (error) {
    return <Card><div role="alert" style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div></Card>;
  }
  if (rows === null) return <Card><Spinner /> Loading…</Card>;

  return (
    <Card>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>Documents across groups</h3>
      <p style={{ margin: "0 0 14px", color: "#6b7280", fontSize: 13 }}>
        Every document every group publishes. The export carries each document's versions
        <strong> and every signature against them</strong>, including names, addresses and IP
        addresses — it is recorded in the log as such.
      </p>
      {/* Where the facility actually looked for the quotations export on
          2026-09-14, and this screen had nothing quotation-shaped on it. A
          panel that silently does not hold the thing somebody came for is what
          produced that task; pointing at the neighbour costs one line. */}
      <p style={{ margin: "0 0 14px", color: "#6b7280", fontSize: 13 }}>
        {word({ plural: true, capital: true })} are not here. Every {word()}, in every state, is downloaded from{" "}
        <strong>{word({ plural: true, capital: true })} export</strong> further down this screen.
      </p>

      {rows.length === 0 ? (
        <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>No documents have been published yet.</p>
      ) : (
        <table className="responsive-table"
               style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
              <th style={{ padding: "8px 10px" }}>Group</th>
              <th style={{ padding: "8px 10px" }}>Title</th>
              <th style={{ padding: "8px 10px" }}>Kind</th>
              <th style={{ padding: "8px 10px" }}>Version</th>
              <th style={{ padding: "8px 10px" }}>Signatures</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map(d => (
              <tr key={d.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td data-label="Group" style={{ padding: "8px 10px" }}>{d.group_name || `group ${d.group_id}`}</td>
                <td data-label="Title" style={{ padding: "8px 10px" }}>
                  {d.title}
                  {d.status === "archived" && <> <Badge color="gray">Withdrawn</Badge></>}
                </td>
                <td data-label="Kind" style={{ padding: "8px 10px" }}>
                  <Badge color={d.mode === "acknowledge" ? "amber" : "gray"}>
                    {d.mode === "acknowledge" ? "Must be acknowledged" : "Notice"}
                  </Badge>
                  {d.blocking && <> <Badge color="red">Blocks transfers</Badge></>}
                </td>
                <td data-label="Version" style={{ padding: "8px 10px", color: "#6b7280" }}>
                  {d.version ? `v${d.version}` : "not published"}
                  {d.versions > 1 && <span style={{ fontSize: 12 }}> of {d.versions}</span>}
                </td>
                <td data-label="Signatures" style={{ padding: "8px 10px", color: "#6b7280" }}>{d.signatures}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>
                  <FileLink path={`/api/admin/documents/${d.id}/archive`} mode="save">
                    Export everything
                  </FileLink>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
